<template>
    <div class="ferry-passenger" v-suppress>
        <span class="ferry-passenger-name">
            {{ name }}
            <span v-if="showGender"> ({{ genderLabel }}) </span>
            <span class="ferry-passenger-dob" v-if="dateOfBirthLabel">
                {{ dateOfBirthLabel }}
            </span>
        </span>
    </div>
</template>
<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { getIconByType } from '@shared/helpers/baggage';
    import { computed, defineComponent, PropType } from 'vue';
    import { useStore } from '@/store';
    import { capitalizeWords } from '@shared/utils/string';
    import { FerryPax, FerryProduct } from '@/api/Cart/cart.interfaces';
    import { convertToDate } from '@shared/helpers/datetime/datetime';
    import { getLocaleDigitDate } from '@shared/helpers/datetime/datetime.formatters';
    import { getAgeRange } from '@/api/Cart/cart.helpers';
    import { AgeRangeDefinitions, PassengerType } from '@/api/Checkout/checkout.interfaces';

    export default defineComponent({
        name: 'FlightModalDetailsPassenger',
        methods: {
            t,
            capitalizeWords,
            getIconByType,
        },
        props: {
            index: {
                type: Number,
                required: true,
            },
            pax: {
                type: Object as PropType<FerryPax>,
                required: true,
            },
            product: {
                type: Object as PropType<FerryProduct>,
                required: true,
            },
        },
        setup(props) {
            const store = useStore();

            const passengerType = computed<PassengerType>(() => {
                const ageDefinitions: AgeRangeDefinitions =
                    store.getters['content/getCheckoutAgeRangeDefinitions'];

                return getAgeRange(props.product, props.pax, ageDefinitions).Type;
            });

            const genderLabel = computed(() => {
                if (!showGender.value) {
                    return undefined;
                }
                return passengerType.value === PassengerType.Adult
                    ? t({
                          key: `shared.gender_titles.adult.${props.pax.Gender}`,
                      })
                    : t({
                          key: `shared.gender_titles.children.${props.pax.Gender}`,
                      });
            });

            const showGender = computed<boolean>(() =>
                Boolean(props.pax.Gender && props.pax.Gender > 0)
            );

            const showFullName = computed<boolean>(() =>
                Boolean(props.pax.FirstName && props.pax.LastName)
            );

            const name = computed(() =>
                showFullName.value
                    ? capitalizeWords(`${props.pax.FirstName} ${props.pax.LastName}`)
                    : `${t({ key: 'shared.traveller' })} ${props.index + 1}`
            );

            const dateOfBirthLabel = computed(() =>
                props.pax.DateOfBirth
                    ? getLocaleDigitDate(convertToDate(props.pax.DateOfBirth))
                    : ''
            );

            return {
                name,
                showGender,
                showFullName,
                dateOfBirthLabel,
                genderLabel,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Passenger.scss';
</style>
